import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { commonStyles } from "../../assets/MUIStyles/commonStyles";
import { ReactComponent as EyeSlashIcon } from "../../assets/icons/eye-slash.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/eye.svg";
import { ReactComponent as ConfirmationIcon } from "../../assets/icons/Confirmation.svg";
import {
  getDecryptedItem,
  setEncryptedItem,
} from "../../utils/localStorageHelper";
import {
  googleLoginAction,
  loginAction,
} from "../NewDevelopment/Actions/CommonActions";
import {
  goToMentionedRoutes,
  LoadingSlides,
} from "../NewDevelopment/CommonComponents/Common";
import {
  checkValidationAccountAction,
  getWhitelabelAction,
} from "../NewDevelopment/Store/Account/Account.action";
import NewAuthLayout from "./NewAuthLayout";
import ErrorPopUp from "../NewDevelopment/CommonComponents/PopUpCards/ErrorPopUp";
import { axiosClient } from "../../Constant/apiConstant";

function NewLogin() {
  const dispatch = useDispatch();
  const history = useHistory();
  const reducer = useSelector((reducer: any) => reducer);
  const {
    AccountReducer: { publicKey },
  } = reducer;
  const [spin, setSpin] = useState(false);
  const [googleButtonDisable, setGooogleButtonDisable] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [state, setState] = useState<any>({
    email: "",
    password: "",
  });
  const { email, password } = state;
  const [errorObj, setErrorObj] = useState({
    show: false,
    errMsg: "",
  });
  const [warningObj, setWarningObj] = useState({
    show: false,
    warningMsg: "",
  });

  useEffect(() => {
    history.push("/");
  }, []);

  useEffect(() => {
    const init = async () => {
      let token = getDecryptedItem("token");
      if (token) {
        setSpin(true);
        let res: any = await dispatch(
          checkValidationAccountAction({ token: `Bearer ${token}` })
        );
        if (res?.errMsg === "authorized") {
          if (res) {
            const { accounts, lastLogin, _id } = res.data;
            let find =
              accounts?.find((acc) => acc?._id === lastLogin?.accountId) ||
              accounts[0];
            if (find && !find.accountName) {
              setEncryptedItem("account-id", find?._id);
              axiosClient.defaults.headers.common["dbUrl"] = find?.accountUrl;
              history.push(`/account-name`);
            } else {
              await goToMentionedRoutes(find, _id, history, dispatch);
            }
          }
        }
      }

      setSpin(false);
    };

    if (publicKey) {
      init();
      getWhiteLabel();
    }
  }, [publicKey]);

  const getWhiteLabel = async () => {
    await dispatch(getWhitelabelAction());
  };

  const signIn = async (e) => {
    e.preventDefault();
    setSpin(true);
    if (!email || !password) {
      let warningMsg = "";
      if (!email && !password) {
        warningMsg = "Email and Password are required.";
      } else if (!email) {
        warningMsg = "Email is required.";
      } else if (!password) {
        warningMsg = "Password is required.";
      }

      setWarningObj({
        show: true,
        warningMsg,
      });
      setSpin(false);
      return;
    }
    try {
      let obj = {
        email: email.trim(),
        password: password,
      };
      let res: any = await dispatch(loginAction(obj));
      if (!res?.token) {
        setErrorObj({
          show: true,
          errMsg: res?.errMsg,
        });
        setSpin(false);
        return;
      }
      if (res) {
        const { accounts, lastLogin, _id } = res;
        let find =
          accounts?.find((acc) => acc?._id === lastLogin?.accountId) ||
          accounts[0];
        if (find && !find.accountName) {
          setEncryptedItem("account-id", find?._id);
          axiosClient.defaults.headers.common["dbUrl"] = find?.accountUrl;
          history.push(`/account-name`);
        } else {
          await goToMentionedRoutes(find, _id, history, dispatch);
        }
      }
    } catch (error) {
      setErrorObj({
        show: true,
        errMsg: "Email or Password is incorrect.",
      });
    }
    setSpin(false);
  };

  const googleResponse = async (response: any) => {
    if (response?.credential) {
      const obj = {
        token: response.credential,
      };
      const res: any = await dispatch(googleLoginAction(obj));
      if (res) {
        if (res) {
          const { accounts, lastLogin, _id } = res;
          const find =
            accounts?.find((acc) => acc?._id === lastLogin?.accountId) ||
            accounts?.[0];
          if (find && !find.accountName) {
            setEncryptedItem("account-id", find?._id);
            axiosClient.defaults.headers.common["dbUrl"] = find?.accountUrl;
            history.push(`/account-name`);
          } else {
            await goToMentionedRoutes(find, _id, history, dispatch);
          }
        }
      }
    }
  };

  const onFailure: any = (error) => {
    if (error?.err) {
      setErrorObj({
        show: true,
        errMsg: error?.err,
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <NewAuthLayout>
      <Typography
        textTransform="capitalize"
        className="font-20-600-lightgrey"
        mb={1}
      >
        Login to your Account
      </Typography>

      <form onSubmit={signIn}>
        <Box mb={2}>
          <Typography className="font-14-grey, mb-1">Email</Typography>
          <TextField
            name="email"
            value={email?.toLowerCase()}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your registered email"
            sx={commonStyles.inputField}
          />
        </Box>
        <Box mb={1}>
          <Typography className="font-14-grey, mb-1">Password</Typography>
          <TextField
            name="password"
            value={password}
            onChange={handleChange}
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            placeholder="Enter your password"
            sx={commonStyles.inputField}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prevState) => !prevState)}
                    edge="end"
                    sx={{ padding: 0, marginRight: 1.5 }}
                    disableRipple
                    disableFocusRipple
                    style={{ outline: "none" }}
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box
            component={"span"}
            onClick={() => history.push("/reset-password")}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              textDecoration: "none",
              cursor: "pointer",
            }}
            mt={2}
            color="#4280FF"
            fontSize={13}
          >
            Forgot Password?
          </Box>
        </Box>
        <Button
          type="submit"
          disabled={spin}
          variant="contained"
          sx={commonStyles.newAuthLoginButton}
          fullWidth
        >
          {spin ? <LoadingSlides bgColor="white" /> : "Login"}
        </Button>
      </form>
      <Box className="google-login mt-2">
        <GoogleLogin
          onSuccess={googleResponse}
          onError={onFailure}
          useOneTap
          theme="outline"
          text="signin_with"
          shape="rectangular"
          logo_alignment="center"
          width="100%"
        />
      </Box>
      <Typography className="font-14-400-grey" textAlign="center" mt={3}>
        Don’t have an account?{" "}
        <Box
          component={"span"}
          onClick={() => history.push("/register")}
          sx={{
            color: "#4280FF",
            fontWeight: "bold",
            cursor: "pointer",
            ":hover": { textDecoration: "underline" },
          }}
        >
          Sign-up
        </Box>
      </Typography>
      {errorObj.show && (
        <ErrorPopUp
          show={errorObj.show}
          toggle={() =>
            setErrorObj((prev) => ({
              ...prev,
              show: false,
              errMsg: "",
            }))
          }
          heading={"Error"}
          message={errorObj?.errMsg}
          spin={spin}
        />
      )}
      {warningObj.show && (
        <ErrorPopUp
          show={warningObj.show}
          toggle={() =>
            setWarningObj((prev) => ({
              ...prev,
              show: false,
              warningMsg: "",
            }))
          }
          heading={"Warning"}
          headerText="Warning"
          message={warningObj?.warningMsg}
          Icon={ConfirmationIcon}
        />
      )}
    </NewAuthLayout>
  );
}

export default NewLogin;
